import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import axiosInstance from '../../services/axiosConfig';

// Types
interface Summary {
  total_retainers_signed: number;
  signed_on_call: number;
  signed_off_call: number;
  on_call_percentage: number;
}

interface LeaderboardEntry {
  firm_user_id: string;
  firm_user_name: string;
  total_retainers: number;
  on_call_signings: number;
  off_call_signings: number;
  on_call_percentage: number;
}

interface TrendData {
  date: string;
  total_signings: number;
  on_call_signings: number;
  off_call_signings: number;
  on_call_percentage: number;
}

interface SigningRecord {
  document_instance_id: string;
  matter_id: string;
  firm_id: string;
  client_signed_at: string;
  signing_context: 'on_call' | 'off_call' | 'unknown';
  call_id: string | null;
  call_start: string | null;
  call_end: string | null;
  call_duration_seconds: number | null;
  firm_user_id: string | null;
  firm_user_name: string | null;
}

interface DashboardData {
  summary: Summary;
  leaderboard: LeaderboardEntry[];
  daily_trends: TrendData[];
  recent_signings: SigningRecord[];
}

// Helper to format date
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (seconds: number | null) => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

export const RetainerAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [days, setDays] = useState(30);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  useEffect(() => {
    fetchDashboard();
  }, [days]);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateTo = new Date();
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - days);

      const response = await axiosInstance.get(
        '/api/zaddy/retainer-analytics/dashboard/',
        {
          params: {
            date_from: dateFrom.toISOString(),
            date_to: dateTo.toISOString(),
          },
        },
      );
      setDashboardData(response.data);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to fetch dashboard data';
      setError(errorMessage || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { summary, leaderboard, daily_trends, recent_signings } = dashboardData;

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Stack spacing={3}>
        {/* Header with controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">Retainer Signing Analytics</Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={days}
                label="Time Range"
                onChange={(e) => setDays(e.target.value as number)}
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={60}>Last 60 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartType}
                label="Chart Type"
                onChange={(e) => setChartType(e.target.value as 'line' | 'bar')}
              >
                <MenuItem value="line">Line Chart</MenuItem>
                <MenuItem value="bar">Bar Chart</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2,
          }}
        >
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Retainers Signed
              </Typography>
              <Typography variant="h3">
                {summary.total_retainers_signed}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: 'success.light' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Signed On Call
              </Typography>
              <Typography variant="h3">{summary.signed_on_call}</Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.on_call_percentage}% of total
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ bgcolor: 'warning.light' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Signed Off Call
              </Typography>
              <Typography variant="h3">{summary.signed_off_call}</Typography>
              <Typography variant="body2" color="text.secondary">
                {(100 - summary.on_call_percentage).toFixed(2)}% of total
              </Typography>
            </CardContent>
          </Card>
          <Card
            sx={{
              bgcolor:
                summary.on_call_percentage >= 50
                  ? 'success.main'
                  : 'error.main',
              color: 'white',
            }}
          >
            <CardContent>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }} gutterBottom>
                On-Call Rate
              </Typography>
              <Typography variant="h3">
                {summary.on_call_percentage}%
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)' }}
              >
                {summary.on_call_percentage >= 50
                  ? 'Above target'
                  : 'Below target'}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Trend Chart */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Daily Signing Trends
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'line' ? (
                <LineChart data={daily_trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    domain={[0, 100]}
                    unit="%"
                  />
                  <Tooltip
                    labelFormatter={(label) => `Date: ${label}`}
                    formatter={(value: number, name: string) => {
                      if (name === 'On-Call %') return [`${value}%`, name];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="total_signings"
                    name="Total"
                    stroke="#8884d8"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="on_call_signings"
                    name="On Call"
                    stroke="#82ca9d"
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="on_call_percentage"
                    name="On-Call %"
                    stroke="#ff7300"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              ) : (
                <BarChart data={daily_trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip labelFormatter={(label) => `Date: ${label}`} />
                  <Legend />
                  <Bar
                    dataKey="on_call_signings"
                    name="On Call"
                    fill="#82ca9d"
                    stackId="stack"
                  />
                  <Bar
                    dataKey="off_call_signings"
                    name="Off Call"
                    fill="#ffc658"
                    stackId="stack"
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Two-column layout for Leaderboard and Recent */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Leaderboard */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              On-Call Signing Leaderboard
            </Typography>
            {leaderboard.length === 0 ? (
              <Typography
                color="text.secondary"
                sx={{ py: 4, textAlign: 'center' }}
              >
                No on-call signings found in this period
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">On-Call</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.slice(0, 10).map((entry, index) => (
                      <TableRow key={entry.firm_user_id}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor:
                                index === 0
                                  ? 'gold'
                                  : index === 1
                                    ? 'silver'
                                    : index === 2
                                      ? '#cd7f32'
                                      : 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 'bold',
                            }}
                          >
                            {index + 1}
                          </Box>
                        </TableCell>
                        <TableCell>{entry.firm_user_name}</TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{ fontWeight: 'bold', color: 'success.main' }}
                          >
                            {entry.on_call_signings}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {entry.total_retainers}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Recent Signings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Signings
            </Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Context</TableCell>
                    <TableCell>Call Duration</TableCell>
                    <TableCell>Agent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent_signings.slice(0, 20).map((record) => (
                    <TableRow key={record.document_instance_id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(record.client_signed_at)}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor:
                              record.signing_context === 'on_call'
                                ? 'success.light'
                                : 'warning.light',
                            display: 'inline-block',
                            fontSize: 12,
                          }}
                        >
                          {record.signing_context === 'on_call'
                            ? 'On Call'
                            : 'Off Call'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDuration(record.call_duration_seconds)}
                      </TableCell>
                      <TableCell>{record.firm_user_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
